<template>
  <section :class="$style.wrapper">
    <header :class="$style.header">
      <h1 :class="$style.title">ファイル</h1>
      <ButtonClose @click="$emit('close')" />
    </header>
    <section :class="$style.body">
      <div :class="$style.actorBlock">
        <h2 :class="$style.subtitle">画像ファイルを受け取る</h2>
        <div :class="$style.description">
          <span>
            ユーザーから画像ファイルを受け取るには以下の URL
            を案内してください。<br />
            {{ uploadPageUrl }}
          </span>

          <div>
            <FlatButton :class="$style.copy" @click="copyUrl">
              <template #icon>
                <IconClipboard :class="$style.copy__icon" />
              </template>
              この URL をコピーする
            </FlatButton>
          </div>
        </div>
      </div>
      <div :class="$style.actorBlock">
        <h2 :class="$style.subtitle">ファイルを送る</h2>
        <div :class="$style.description">
          <div>
            <FileUpload400FOA
              :room-id="roomId"
              :user-name="userName"
              @uploaded="refetchFilesFor400FOaResult"
            />
          </div>
        </div>
      </div>
      <div :class="$style.list">
        <h2 :class="$style.subtitle">送受信ファイル一覧</h2>
        <div v-for="file in files" :key="file.id" :class="$style.file">
          <a @click="() => requestDownload(file.id)" :class="$style.download">
            <span :class="$style.file__name">{{ file.name }}</span>
          </a>
          <div style="display: flex; gap: 8px; margin-top: 8px; flex-shrink: 0">
            <div :class="$style.file__attr">{{ prettyBytes(file.size) }}</div>
            <div :class="$style.file__attr">
              {{ formatDate(file.uploadedAt) }}
            </div>
          </div>
          <button
            :class="$style.sendDownloadUrl"
            @click="() => requestSendDownloadUrl(file.id)"
          >
            <IconPaperPlane />
            ダウンロードURLを送る
          </button>
        </div>
      </div>
    </section>
  </section>
</template>

<script lang="ts" setup>
import IconClipboard from '@/components/icons/IconClipboard.vue'
import IconPaperPlane from '@/components/icons/IconPaperPlane.vue'
import ButtonClose from '@/components/atoms/ButtonClose.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { useAlert } from '@/libs/alertPortal'
import { type RoomID } from '@/types/app'
import { format } from 'date-fns'
import { computed } from 'vue'
import {
  useIssueFileDownloadUrlOnRequestFileUploadForOaModalMutation,
  useChatRoomFor400FOaOnRequestFileUploadForOaModalQuery,
  useSendFileDownloadUrlOnRequestFileUploadForOaModalMutation,
} from './RequestFileUploadForOAModal.generated'
import prettyBytes from 'pretty-bytes'
import FileUpload400FOA from './FileUpload400FOA.vue'

const props = defineProps<{ roomId: RoomID; userName: string }>()

const { result: filesFor400FOaResult, refetch: refetchFilesFor400FOaResult } =
  useChatRoomFor400FOaOnRequestFileUploadForOaModalQuery(
    {
      roomId: props.roomId.toString(),
    },
    {
      // ユーザーからファイルをアップロードされたとき、適切なタイミングでキャッシュを破棄することが難しいのでそもそもキャッシュしない
      fetchPolicy: 'no-cache',
    }
  )

const uploadPageUrl = computed(() => {
  return filesFor400FOaResult.value?.ownChatRoom?.userUploadPageUrl ?? ''
})

const files = computed(() => {
  return (
    filesFor400FOaResult.value?.ownChatRoom?.filesFor400FOa?.map(file => {
      return {
        ...file,
        uploadedAt: new Date(file.uploadedAt),
      }
    }) ?? []
  )
})

const formatDate = (date: Date) => format(date, 'yyyy年MM月dd日 HH:mm')

const alert = useAlert()

const copyUrl = async () => {
  await navigator.clipboard.writeText(uploadPageUrl.value)

  alert.success('ファイルアップロード画面の URL をコピーしました')
}

const { mutate: issueFileDownloadUrl } =
  useIssueFileDownloadUrlOnRequestFileUploadForOaModalMutation({})

const requestDownload = async (id: string) => {
  if (
    !window.confirm(
      `ファイルを開きますか？\n\n※ファイルには有効期限があります。有効期限が切れた場合は再度 一覧から開き直してください。`
    )
  ) {
    return
  }

  const res = await issueFileDownloadUrl({
    id: id,
  })

  if (res?.data) {
    window.open(res.data.for400FOA__issueFileDownloadURL, '_blank')
  }
}

const sendFileDownloadUrl =
  useSendFileDownloadUrlOnRequestFileUploadForOaModalMutation({})

const requestSendDownloadUrl = async (id: string) => {
  const file = files.value.find(f => f.id === id)
  if (!file) {
    return
  }

  const confirmed = window.confirm(
    `${props.userName}さんに「${file.name}」のダウンロードURLを送信しますか？`
  )
  if (!confirmed) {
    return
  }

  const res = await sendFileDownloadUrl.mutate({
    id: id,
  })

  if (!res?.data?.for400FOA__sendFileDownloadURL.isSucceeded) {
    alert.error('ダウンロードURLの送信に失敗しました')
    return
  }

  alert.success('ダウンロードURLを送信しました')
}
</script>

<style lang="scss" module>
.wrapper {
  width: 90vw;
  max-width: 620px;
}

.description {
  background-color: $color-surface-tertiary;
  border: 1px solid $color-surface-secondary;
  padding: 16px;
  border-radius: $border-radius-v2-base;
}

.header {
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid $color-border-low-emphasis;
}

.title {
  font-size: 16px;
  font-weight: bold;
}

.subtitle {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
}

.body {
  padding: 10px 40px 40px;

  &__buttonWrapper {
    margin-top: 44px;
  }
}

.actorBlock {
  &:nth-child(n + 2) {
    margin-top: 16px;
  }
}

.addButton {
  display: block;
  margin-top: 10px;
}

.copy {
  margin-top: 16px;

  &__icon {
    font-size: 18px;
  }
}

.fileSelect {
  &__icon {
    font-size: 18px;
  }
}

.list {
  margin-top: 20px;
}

.item {
  &:nth-child(n + 2) {
    margin-top: 20px;
  }
}

.file {
  padding: 16px 0;

  &:nth-child(n + 2) {
    border-top: 1px solid $color-border-low-emphasis;
  }

  &__name {
    word-break: break-all;
  }

  &__attr {
    color: #999999;
  }
}

.download {
  cursor: pointer;
  font-weight: bold;

  &__icon {
    font-size: 18px;
    line-height: 20px;
  }
}

.sendDownloadUrl {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: $color-text-and-object-accent-primary;

  &:hover {
    text-decoration: underline;
  }
}
</style>
